/**
 * ============================================================================
 * LINK DETAIL PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for managing a specific transaction's link
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserDashboardLayout } from '../components/user';
import { useTransaction } from '../hooks/useTransaction';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import LinkManager from '../components/links/LinkManager';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';

/**
 * Link Detail Page
 * @returns {JSX.Element}
 */
const LinkDetail = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { getTransaction, transaction, loading } = useTransaction();
  const { showError } = useToast();
  const { language } = useI18n();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await getTransaction(transactionId);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement de la transaction' : 'Failed to load transaction')
        );
        console.error('Load transaction error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (transactionId) {
      loadData();
    }
  }, [transactionId, getTransaction, showError, language]);

  if (isLoading || loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  if (!transaction) {
    return (
      <UserDashboardLayout>
        <Card variant="glass" className="p-12 text-center">
          <EmptyState
            icon="❌"
            title={language === 'fr' ? 'Transaction introuvable' : 'Transaction not found'}
            description={
              language === 'fr'
                ? 'La transaction demandée n\'existe pas ou vous n\'y avez pas accès'
                : 'The requested transaction does not exist or you do not have access to it'
            }
          />
          <Button
            variant="outline"
            onClick={() => navigate('/links')}
            className="mt-4"
          >
            ← {language === 'fr' ? 'Retour aux liens' : 'Back to Links'}
          </Button>
        </Card>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/links')}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour aux liens' : 'Back to Links'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          🔗 {language === 'fr' ? 'Gestion du lien' : 'Link Management'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? `Gérez le lien pour: ${transaction.title}`
            : `Manage link for: ${transaction.title}`}
        </p>
      </div>

      {/* Link Manager Component */}
      <div className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <LinkManager transaction={transaction} transactionId={transactionId} />
      </div>
    </UserDashboardLayout>
  );
};

export default LinkDetail;
